// SMS (4 October 2026): broadcasts by SMS, a flow's SMS step, SMS when WhatsApp cannot deliver a service
// message, and team notifications, through whichever provider the platform chose.
//
// `services/sms`. Hand-typed until the generated client is next fetched from a running stack. The request
// path is the house core, so the envelope, token refresh and ApiError mapping are the same as everywhere.
//
// A console asks `getStatus()` before it offers anything SMS: `available: false` means the platform has no
// provider set up, and every SMS option stays hidden.

import { http } from './_core/request.js';
import type { Envelope } from './_core/envelope.js';

export interface SmsStatus {
  /** Whether SMS can be sent on the platform at all. */
  available: boolean;
  /** Whether this organisation's WhatsApp service messages fall back to SMS. */
  fallback_enabled: boolean;
}

export interface SmsSettings {
  /** WhatsApp utility and authentication messages that cannot be delivered go by SMS instead. Off by default. */
  fallback_enabled: boolean;
  updated_at?: string | null;
}

/** What a text becomes as an SMS: the business name first, the opt-out line on marketing, measured in parts. */
export interface SmsPreview {
  body: string;
  /** `gsm7` (160 characters a part, 153 once split) or `ucs2` (70, 67), when anything outside the GSM alphabet is used. */
  encoding: 'gsm7' | 'ucs2';
  /** The parts one message takes. Each part is charged. */
  segments: number;
  characters: number;
  /** The most parts a message may take; a longer one is refused. */
  max_segments: number;
}

/** One SMS's outcome, as the platform console's test send reports it. */
export interface SmsSendResult {
  message_id?: string | null;
  status: 'queued' | 'sent' | 'delivered' | 'failed' | 'refused';
  error_code?: string | null;
  error?: string | null;
  segments: number;
  charged_minor: number;
  duplicate: boolean;
}

export interface SmsAdminStatus {
  available: boolean;
  active_provider?: string | null;
  backup_provider?: string | null;
  /** Why SMS is not available, in words. */
  reason?: string | null;
  last_24_hours: { status: string; count: number }[];
  last_webhook?: { provider_code: string; received_at: string; outcome?: string | null } | null;
}

export function getStatus(): Promise<Envelope<SmsStatus>> {
  return http.get<Envelope<SmsStatus>>('/v1/sms/status');
}

export function getSettings(): Promise<Envelope<SmsSettings>> {
  return http.get<Envelope<SmsSettings>>('/v1/sms/settings');
}

/** Owner and admins only (an agent is refused 403). */
export function updateSettings(req: { fallback_enabled: boolean }): Promise<Envelope<SmsSettings>> {
  return http.put<Envelope<SmsSettings>>('/v1/sms/settings', { body: req });
}

/** `marketing` adds the opt-out line ("Reply STOP to opt out"), which counts towards the parts. */
export function preview(req: { text: string; marketing?: boolean }): Promise<Envelope<SmsPreview>> {
  return http.post<Envelope<SmsPreview>>('/v1/sms/preview', { body: req });
}

/** Platform console: routing, the last day's sends by status and the last webhook. */
export function adminStatus(): Promise<Envelope<SmsAdminStatus>> {
  return http.get<Envelope<SmsAdminStatus>>('/v1/admin/sms/status');
}

/** Platform console: a test SMS through the chosen provider. Charged to nobody. */
export function adminTest(req: { to: string; text?: string }): Promise<Envelope<SmsSendResult>> {
  return http.post<Envelope<SmsSendResult>>('/v1/admin/sms/test', { body: req });
}
